import React, { useContext, useEffect, useState } from "react";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";
import AuthContext from "../store/authContext";
import OrgContext from "../store/orgContext";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import useHideSidePanel from "../hooks/useHideSidePanel";
import InfoAlert from "../ui/InfoAlert";
import ViewRow from "../components/dataTable/ViewRow";
import { VIEW_P25_AFFILIATIONS_FORM_FIELDS } from "../constants/viewFormFields";
import {
  createSearchConfig,
  errorAlert,
  makeApiRequest,
  transformP25AffiliationsResponseRow,
  transformP25GAffiliationsData,
} from "../util";
import { getP25AffiliationDataByRef } from "../api";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import { P25_AFFILIATIONS_FILTER_COLUMNS } from "../constants/filterColumns";
import TableActionButton from "../ui/TableActionButton";
import { P25_AFFILIATIONS_TABLE_HEADERS } from "../constants/tableHeaders";
import { READ_P25_AFFILIATIONS } from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import SearchContext from "../store/searchContext";

interface ISearchData {
  RFSS?: string | null;
  "TARGET-ID"?: string | null;
}

const INITIAL_STATE = {
  RFSS: "",
  "TARGET-ID": "",
};

const searchFields = ["RFSS", "TARGET-ID"];

const searchConfigNew = createSearchConfig(
  searchFields,
  VIEW_P25_AFFILIATIONS_FORM_FIELDS
);

const P25Affiliations = () => {
  const { token } = useContext(AuthContext);
  const { updateOrg, selectedOrg, orgs: orgList } = useContext(OrgContext);
  const { selectedColumn, setSelectedColumn, setSearchText, setDisableSearch } =
    useContext(SearchContext);
  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  useAppTitle("P25 Group Affiliations- IWF Config Admin Tool (ICAT) by AT&T");
  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
  }>({
    data: {},
    pageSize: 20,
    pageNum: 1,
  });
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [affiliationData, setAffiliatioData] = useState<any[]>([]);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);

  const fetchP25AffiliationData = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize } = searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(data &&
            data.RFSS && {
              RFSS: data.RFSS,
            }),
          ...(data &&
            data["TARGET-ID"] && {
              "TARGET-ID": data["TARGET-ID"],
            }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/realtime/p25/group-aff?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        setNumberOfRows(response.NbrOfRows);
        setNumberOfPages(response.NbrOfPages);
        setAffiliatioData(response.Rows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchData(INITIAL_STATE);
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    fetchP25AffiliationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedOrg]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  return (
    <>
      <SectionHeader
        pageTitle={READ_P25_AFFILIATIONS.header}
        pageDescription={READ_P25_AFFILIATIONS.headerSubtext}
        sideComponent={
          <div>
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />
      {selectedOrg ? (
        <>
          {showAdvancedSearch ? (
            <AdvancedSearch
              setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
              advancedSearchConfig={searchConfigNew}
              advancedSearchState={searchData}
              setAdvancedSearchState={setSearchData}
              onCancel={() => {
                setSearchData(INITIAL_STATE);
                handleToggleAdvanceSearchPanel();
              }}
              onSubmit={(data) =>
                setSearhQuery({
                  ...searchQuery,
                  pageNum: 1,
                  pageSize: 20,
                  data,
                })
              }
            />
          ) : null}
          {showSidePanel && selectedConnectionId ? (
            <ViewRow
              panelName={READ_P25_AFFILIATIONS.viewRow.panelName}
              org={selectedOrg.id}
              esRef={selectedConnectionId}
              formFields={VIEW_P25_AFFILIATIONS_FORM_FIELDS}
              loadDataByRef={getP25AffiliationDataByRef}
              transformResponseRow={transformP25AffiliationsResponseRow}
              setShowEditPanel={handleToggleSidePanel}
            />
          ) : null}
          <DataTableWithControls
            loading={loading}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            filterColumns={P25_AFFILIATIONS_FILTER_COLUMNS}
            onSearch={(term) =>
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: { [`${selectedColumn?.id}`]: term },
              })
            }
            showCreateButton={false}
            headers={P25_AFFILIATIONS_TABLE_HEADERS}
            rows={transformP25GAffiliationsData(affiliationData).map(
              (row: { id: string }) => ({
                ...row,
                action: (
                  <TableActionButton
                    onActionButtonClick={() => {
                      setSelectedConnectionId(row.id);
                      handleToggleSidePanel();
                    }}
                  />
                ),
              })
            )}
            showTableControls
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            numberOfAdvancedSearchFilters={searchConfigNew.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc, el: any) => {
                const { name } = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj: any = { ...searchData };
                return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
              },
              0
            )}
            clearAdvancedSearchFilters={() => {
              setSearchData(INITIAL_STATE);
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: {},
              });
            }}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
          />
        </>
      ) : (
        <InfoAlert
          heading={READ_P25_AFFILIATIONS.infoAlert.heading}
          subText={READ_P25_AFFILIATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default P25Affiliations;
